import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { uploadArtFile, artUrl } from "@/lib/uploadArt";
import { usePersistedState } from "@/hooks/usePersistedState";
import InkEstimateCard from "@/components/studio/InkEstimateCard";
import SaveToLibraryButton from "@/components/studio/SaveToLibraryButton";
import { confirmAiCost } from "@/lib/aiCost";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { HelpHint } from "@/components/HelpHint";
import { Upload, CheckCircle2, Loader2, FileDown, Link2, Link2Off, Ruler, Pipette, Circle, Sparkles } from "lucide-react";
import { toast } from "sonner";

const MODES = [
  { id: "fill", label: "Fill (crop)", hint: "Cover the whole size, cropping the overflow. Best for photos/banners.", fit: "cover" },
  { id: "fit", label: "Fit (letterbox)", hint: "Show the whole image inside the size, with a margin all around (fills the rest with the background color).", fit: "contain" },
  { id: "stretch", label: "Stretch", hint: "Force the image to the exact size (may distort proportions).", fit: "fill" },
];

const GROMMET_LAYOUTS = [
  { id: "corners", label: "4 corners" },
  { id: "corners_top_center", label: "4 corners + top center" },
  { id: "corners_2center", label: "4 corners + 2 center (top & bottom)" },
  { id: "corners_midsides", label: "4 corners + 4 mid-sides" },
  { id: "perimeter_2ft", label: "Perimeter every 2 ft" },
  { id: "top_corners", label: "2 top corners" },
  { id: "top_center", label: "Top center only" },
];

const FIT_LETTERBOX_SCALE = 0.85; // must match backend

const perimeterPoints = (w, h, d, step) => {
  const x0 = d, y0 = d, x1 = w - d, y1 = h - d;
  const iw = Math.max(0, x1 - x0), ih = Math.max(0, y1 - y0);
  const nx = iw > 0 ? Math.max(1, Math.round(iw / step)) : 1;
  const ny = ih > 0 ? Math.max(1, Math.round(ih / step)) : 1;
  const map = new Map();
  const add = (x, y) => map.set(`${x.toFixed(3)},${y.toFixed(3)}`, [x, y]);
  for (let i = 0; i <= nx; i++) { const x = x0 + iw * i / nx; add(x, y0); add(x, y1); }
  for (let j = 0; j <= ny; j++) { const y = y0 + ih * j / ny; add(x0, y); add(x1, y); }
  return [...map.values()];
};

const grommetPoints = (layout, w, h, d) => {
  const corners = [[d, d], [w - d, d], [d, h - d], [w - d, h - d]];
  const cx = w / 2, cy = h / 2;
  switch (layout) {
    case "corners": return corners;
    case "corners_top_center": return [...corners, [cx, d]];
    case "corners_2center": return [...corners, [cx, d], [cx, h - d]];
    case "corners_midsides": return [...corners, [cx, d], [cx, h - d], [d, cy], [w - d, cy]];
    case "perimeter_2ft": return perimeterPoints(w, h, d, 24);
    case "top_corners": return [[d, d], [w - d, d]];
    case "top_center": return [[cx, d]];
    default: return [];
  }
};

const isImageName = (n = "") => /\.(png|jpe?g|webp|gif|bmp)$/i.test(n);

const readImgDims = (src) => new Promise((resolve) => {
  const im = new Image();
  im.onload = () => resolve({ w: im.naturalWidth, h: im.naturalHeight });
  im.onerror = () => resolve({ w: 0, h: 0 });
  im.src = src;
});

export const FitSizeTab = () => {
  const [st, setSt] = usePersistedState("pns_studio_fitsize", {
    fileName: "", uploaded: null, width: "48", height: "24", unit: "in", bleed: "0",
    mode: "fill", locked: false, ratio: 2, result: null, imgW: 0, imgH: 0, targetDpi: "0",
    bgColor: "#ffffff", trimWhite: false, grommetsOn: false, grommetLayout: "corners", grommetInset: "1", grommetColor: "dark",
    expandOpen: false, mL: "0", mR: "0", mT: "0", mB: "0",
  });
  const patch = (p) => setSt((s) => ({ ...s, ...p }));
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const {
    fileName, uploaded, width, height, unit, bleed, mode, locked, result, imgW, imgH, targetDpi,
    bgColor, trimWhite, grommetsOn, grommetLayout, grommetInset, grommetColor, expandOpen,
  } = st;

  const onPick = async (f) => {
    if (!f) return;
    patch({ fileName: f.name, result: null, uploaded: null });
    try {
      setBusy(true);
      const up = await uploadArtFile(f);
      const isImg = (f.type || "").startsWith("image") || isImageName(f.name);
      patch({ uploaded: { ...up, isImage: isImg } });
      if (isImg) {
        const d = await readImgDims(URL.createObjectURL(f));
        const curW = parseFloat(width) || 0;
        const newH = (d.w > 0 && d.h > 0 && curW > 0) ? Math.round((curW * d.h / d.w) * 100) / 100 : height;
        patch({ imgW: d.w, imgH: d.h, height: String(newH) });
      } else patch({ imgW: 0, imgH: 0 });
      toast.success("Image uploaded");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setW = (v) => {
    if (locked) {
      const r = +st.ratio;
      const h = (+v > 0 && r > 0) ? Math.round((+v / r) * 100) / 100 : height;
      patch({ width: v, height: String(h) });
    } else patch({ width: v });
  };
  const setH = (v) => {
    if (locked) {
      const r = +st.ratio;
      const w = (+v > 0 && r > 0) ? Math.round((+v * r) * 100) / 100 : width;
      patch({ height: v, width: String(w) });
    } else patch({ height: v });
  };
  const toggleLock = () => {
    if (!locked) {
      const w = parseFloat(width), h = parseFloat(height);
      patch({ locked: true, ratio: (w > 0 && h > 0) ? w / h : 1 });
    } else patch({ locked: false });
  };

  const pickColor = async () => {
    if (!window.EyeDropper) { toast.error("Eyedropper isn't supported in this browser — use the color box instead."); return; }
    try {
      const ed = new window.EyeDropper();
      const { sRGBHex } = await ed.open();
      patch({ bgColor: sRGBHex });
    } catch { /* cancelled */ }
  };

  const generate = async () => {
    if (!uploaded?.file_id) { toast.error("Upload an image first"); return; }
    const w = parseFloat(width), h = parseFloat(height);
    if (!(w > 0) || !(h > 0)) { toast.error("Enter a valid width and height"); return; }
    setBusy(true); patch({ result: null });
    try {
      const fd = new FormData();
      fd.append("file_id", uploaded.file_id);
      fd.append("width", String(w));
      fd.append("height", String(h));
      fd.append("unit", unit);
      fd.append("bleed", String(parseFloat(bleed) || 0));
      fd.append("mode", mode);
      fd.append("bg_color", bgColor);
      fd.append("trim_white", trimWhite ? "true" : "false");
      fd.append("grommet_layout", grommetsOn ? grommetLayout : "none");
      fd.append("grommet_inset", String(parseFloat(grommetInset) || 0));
      fd.append("grommet_color", grommetColor);
      fd.append("target_dpi", targetDpi || "0");
      const r = await api.post("/studio/fit-to-size", fd, { headers: { "Content-Type": "multipart/form-data" } });
      patch({ result: r.data });
      toast.success("Print-ready PDF generated");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const expandAI = async (auto) => {
    if (!uploaded?.file_id) { toast.error("Upload an image first"); return; }
    if (!uploaded?.isImage) { toast.error("AI expand works on images (PNG/JPG)"); return; }
    if (!window.confirm) { return; }
    if (!confirmAiCost("AI Expand")) return;
    setAiBusy(true);
    try {
      const fd = new FormData();
      fd.append("file_id", uploaded.file_id);
      fd.append("unit", unit);
      fd.append("width", String(parseFloat(width) || 1));
      fd.append("height", String(parseFloat(height) || 1));
      if (auto) { fd.append("expand_mode", "auto"); }
      else {
        fd.append("expand_mode", "manual");
        fd.append("margin_left", String(parseFloat(st.mL) || 0));
        fd.append("margin_right", String(parseFloat(st.mR) || 0));
        fd.append("margin_top", String(parseFloat(st.mT) || 0));
        fd.append("margin_bottom", String(parseFloat(st.mB) || 0));
      }
      const r = await api.post("/studio/expand-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      patch({ uploaded: { file_id: r.data.file_id, url: r.data.url, isImage: true }, fileName: "expanded.png", result: null });
      readImgDims(artUrl(r.data.file_id)).then((d) => patch({ imgW: d.w, imgH: d.h }));
      toast.success("Background expanded — now using the AI result");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setAiBusy(false); }
  };

  const onTrialUpload = async (f) => {
    if (!f) return;
    setAiBusy(true);
    try {
      const up = await uploadArtFile(f);
      patch({ uploaded: { ...up, isImage: true }, fileName: f.name, result: null });
      readImgDims(URL.createObjectURL(f)).then((d) => patch({ imgW: d.w, imgH: d.h }));
      toast.success("Using your uploaded expansion");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setAiBusy(false); }
  };

  const objFit = MODES.find((m) => m.id === mode)?.fit || "cover";
  const w = parseFloat(width) || 0, h = parseFloat(height) || 0;
  const b = parseFloat(bleed) || 0;
  const ow = w + 2 * b, oh = h + 2 * b;
  const gd = parseFloat(grommetInset) || 0;
  const modeLabel = MODES.find((m) => m.id === mode)?.label;
  const canPreview = uploaded?.file_id && uploaded?.isImage && w > 0 && h > 0;
  const imgSrc = uploaded?.file_id ? artUrl(uploaded.file_id) : null;
  const dpiVal = (imgW > 0 && ow > 0 && oh > 0) ? Math.round(Math.min(imgW / ow, imgH / oh)) : 0;
  const maxDimIn = Math.max(ow, oh);
  const suggestedDpi = maxDimIn <= 24 ? 300 : maxDimIn <= 60 ? 150 : maxDimIn <= 120 ? 100 : 72;
  const recOption = suggestedDpi <= 150 ? "150" : "300";
  const dpiEnough = dpiVal >= suggestedDpi;
  const imgStyle = { objectFit: objFit };
  if (mode === "fit") imgStyle.transform = `scale(${FIT_LETTERBOX_SCALE})`;

  return (
    <div className="space-y-5 max-w-4xl" data-testid="fit-size-tab">
      <p className="text-sm text-slate-500 flex items-center gap-2">Upload an image and set the exact size — no template needed.
        <HelpHint side="right">Type the final print size (e.g. 24×48 in). The image auto-adjusts to that size and exports a production-ready PDF. Use <b>Fill</b> to cover &amp; crop, <b>Fit</b> to keep the whole image with a margin, or <b>Stretch</b> for an exact fit. Turn on <b>Grommets</b> to mark where they go.</HelpHint>
      </p>

      <label className="flex flex-col items-center justify-center gap-2 h-32 rounded-xl border-2 border-dashed border-slate-300 cursor-pointer hover:border-[#2495D3] text-sm text-slate-500 bg-slate-50/50" data-testid="fit-size-drop">
        {fileName ? <><CheckCircle2 size={20} className="text-emerald-500" /><span className="max-w-[240px] truncate">{fileName}</span><span className="text-[11px] text-slate-400">Click to replace</span></>
              : <><Upload size={22} /><span>Upload image (PNG / JPG / PDF)</span><span className="text-[11px] text-slate-400">Up to 150 MB</span></>}
        <input data-testid="fit-size-file" type="file" accept="application/pdf,image/png,image/jpeg" className="hidden" onChange={(e) => { onPick(e.target.files?.[0]); e.target.value = ""; }} />
      </label>

      {/* Size row with chain link between Width and Height */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex items-end gap-2">
          <div>
            <label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Width</label>
            <Input type="number" step="any" min="0" value={width} onChange={(e) => setW(e.target.value)} className="w-28" data-testid="fit-size-width" />
          </div>
          <button type="button" onClick={toggleLock} title={locked ? "Aspect ratio locked — editing one side scales the other" : "Lock aspect ratio"}
            className={`mb-2 transition-colors ${locked ? "text-[#2495D3]" : "text-slate-300 hover:text-slate-500"}`} data-testid="fit-size-lock" aria-label="lock-aspect-ratio">
            {locked ? <Link2 size={18} /> : <Link2Off size={18} />}
          </button>
          <div>
            <label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Height</label>
            <Input type="number" step="any" min="0" value={height} onChange={(e) => setH(e.target.value)} className="w-28" data-testid="fit-size-height" />
          </div>
        </div>
        <div>
          <label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Unit</label>
          <select className="h-10 rounded-lg border px-2 text-sm w-28" value={unit} onChange={(e) => patch({ unit: e.target.value })} data-testid="fit-size-unit">
            <option value="in">inches</option>
            <option value="cm">cm</option>
            <option value="mm">mm</option>
            <option value="ft">feet</option>
          </select>
        </div>
        <div>
          <label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block flex items-center gap-1">Bleed / side
            <HelpHint side="top">Bleed is added <b>per side</b> (perimeter). E.g. 48×24 with a 2" bleed extends 2" on every edge → final file <b>52×28</b>. The red frame in the preview marks the true trim size; artwork bleeds past it. Leave 0 for exact size.</HelpHint>
          </label>
          <Input type="number" step="any" min="0" value={bleed} onChange={(e) => patch({ bleed: e.target.value })} className="w-28" data-testid="fit-size-bleed" />
        </div>
      </div>

      {/* Adjust mode + Grommets toggle */}
      <div>
        <label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Adjust mode</label>
        <div className="flex flex-wrap gap-2" data-testid="fit-size-modes">
          {MODES.map((mo) => (
            <button key={mo.id} type="button" onClick={() => patch({ mode: mo.id })} title={mo.hint}
              className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${mode === mo.id ? "border-[#2495D3] bg-[#2495D3]/10 text-[#2495D3]" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}
              data-testid={`fit-size-mode-${mo.id}`}>{mo.label}</button>
          ))}
          <button type="button" onClick={() => patch({ grommetsOn: !grommetsOn })} title="Add grommet placement marks"
            className={`px-3 py-1.5 rounded-lg text-xs border transition-colors inline-flex items-center gap-1 ${grommetsOn ? "border-red-500 bg-red-50 text-red-600" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}
            data-testid="fit-size-grommets-toggle"><Circle size={12} /> Grommets</button>
        </div>
        <p className="text-[11px] text-slate-400 mt-1">{MODES.find((m) => m.id === mode)?.hint}</p>
      </div>

      {/* Grommet config */}
      {grommetsOn && (
        <div className="flex flex-wrap items-end gap-3 rounded-lg border border-red-200 bg-red-50/40 p-3" data-testid="fit-size-grommet-config">
          <div>
            <label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Grommet layout</label>
            <select className="h-9 rounded-lg border px-2 text-sm bg-white" value={grommetLayout} onChange={(e) => patch({ grommetLayout: e.target.value })} data-testid="fit-size-grommet-layout">
              {GROMMET_LAYOUTS.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block flex items-center gap-1">Distance from edge ({unit})
              <HelpHint side="top">How far the grommet center sits from the trim edge (inside the red frame). Marks are added to the production PDF so the shop knows exactly where to punch them.</HelpHint>
            </label>
            <Input type="number" step="any" min="0" value={grommetInset} onChange={(e) => patch({ grommetInset: e.target.value })} className="w-28" data-testid="fit-size-grommet-inset" />
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Marker color</label>
            <select className="h-9 rounded-lg border px-2 text-sm bg-white" value={grommetColor} onChange={(e) => patch({ grommetColor: e.target.value })} data-testid="fit-size-grommet-color">
              <option value="dark">Dark</option>
              <option value="light">Light</option>
            </select>
          </div>
          <p className="text-[11px] text-slate-400 w-full">Dots mark grommet centers, added to the PDF at ≤0.25&quot; diameter in your chosen color.</p>
        </div>
      )}

      {/* Edges & background (matters for Fit) */}
      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer" title="Auto-crop a white border off the image before placing">
          <input type="checkbox" checked={trimWhite} onChange={(e) => patch({ trimWhite: e.target.checked })} data-testid="fit-size-trim-white" />
          Trim white edges
        </label>
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-mono uppercase text-slate-500">Background</span>
          <input type="color" value={bgColor} onChange={(e) => patch({ bgColor: e.target.value })} className="w-9 h-8 rounded border p-0 cursor-pointer" data-testid="fit-size-bg-color" title="Fill color for Fit margins" />
          <Button type="button" size="sm" variant="outline" onClick={pickColor} data-testid="fit-size-eyedropper" title="Pick a color from the screen"><Pipette size={14} className="mr-1" /> Eyedropper</Button>
          <HelpHint side="top">Used to fill the margin around the image in <b>Fit</b> mode. Use the eyedropper to sample the color from the image edge so the fill blends in (removes the white border look).</HelpHint>
        </div>
      </div>

      {/* Output quality / upscale */}
      {uploaded?.file_id && uploaded?.isImage && (
        <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="fit-size-quality">
          <span className="text-[11px] font-mono uppercase text-slate-500">Output quality</span>
          <select className="h-9 rounded-lg border px-2 text-sm bg-white" value={targetDpi} onChange={(e) => patch({ targetDpi: e.target.value })} data-testid="fit-size-target-dpi">
            <option value="0">Keep original</option>
            <option value="150">Upscale to 150 DPI (large format)</option>
            <option value="300">Upscale to 300 DPI (photo / print)</option>
          </select>
          <HelpHint side="top">Free resample (Lanczos) up to the target DPI so the PDF isn't pixelated. It does not add real detail — final sharpness is limited by the original image.</HelpHint>
          {imgW > 0 && (dpiEnough
            ? <span className="text-[11px] text-emerald-600" data-testid="fit-size-dpi-suggest">Recommended ~{suggestedDpi} DPI for {ow}×{oh}{unit} — your image is enough, keep original.</span>
            : <button type="button" className="text-[11px] text-[#2495D3] underline" onClick={() => patch({ targetDpi: recOption })} data-testid="fit-size-dpi-suggest">Recommended ~{suggestedDpi} DPI for {ow}×{oh}{unit} — tap to upscale to {recOption} DPI</button>)}
          {dpiVal > 0 && dpiVal < 150 && <span className="text-[11px] text-red-600 font-medium" data-testid="fit-size-lowres">⚠ Low resolution: {dpiVal} DPI — turn on Upscale or use a larger image before printing.</span>}
        </div>
      )}

      {/* Expand background with AI (outpainting) */}
      {uploaded?.file_id && uploaded?.isImage && (
        <div className="rounded-lg border border-[#2495D3]/30 bg-[#2495D3]/5 p-3 space-y-2" data-testid="ai-expand-panel">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <Sparkles size={15} className="text-[#2495D3]" /> Expand background with AI
            <HelpHint side="top">Instead of a solid color, the AI extends the photo's background so the added area looks like part of the same image (generative expand / outpainting). Uses Universal Key credits — you'll confirm before it runs.</HelpHint>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button size="sm" onClick={() => expandAI(true)} disabled={aiBusy} data-testid="ai-expand-auto">
              {aiBusy ? <Loader2 size={14} className="animate-spin mr-1" /> : <Sparkles size={14} className="mr-1" />}Expand to fill {w || "?"}×{h || "?"} {unit}
            </Button>
            <button type="button" className="text-xs text-slate-500 underline hover:text-[#2495D3]" onClick={() => patch({ expandOpen: !expandOpen })} data-testid="ai-expand-manual-toggle">Manual margins</button>
            <label className="text-xs text-slate-500 underline hover:text-[#2495D3] cursor-pointer" data-testid="ai-expand-trial-label">Upload expansion (no credits)
              <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => { onTrialUpload(e.target.files?.[0]); e.target.value = ""; }} data-testid="ai-expand-trial" />
            </label>
          </div>
          {expandOpen && (
            <div className="flex flex-wrap items-end gap-2 pt-1">
              {[["mL", "Left"], ["mR", "Right"], ["mT", "Top"], ["mB", "Bottom"]].map(([k, lab]) => (
                <div key={k}>
                  <label className="text-[10px] font-mono uppercase text-slate-500 block mb-0.5">{lab} ({unit})</label>
                  <Input type="number" step="any" min="0" value={st[k]} onChange={(e) => patch({ [k]: e.target.value })} className="w-20" data-testid={`ai-expand-${k}`} />
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => expandAI(false)} disabled={aiBusy} data-testid="ai-expand-manual-go">Expand (AI)</Button>
            </div>
          )}
          <p className="text-[10px] text-slate-400">Tip: after expanding, switch to <b>Fill</b> so the new image covers the size edge-to-edge with no white margin.</p>
        </div>
      )}

      {/* Before / After preview */}
      {uploaded?.file_id && (
        <div data-testid="fit-size-preview">
          <div className="text-[11px] font-mono uppercase text-slate-500 mb-2 flex items-center gap-1"><Ruler size={12} /> Preview — before &amp; after</div>
          {canPreview ? (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[11px] text-slate-400 mb-1 flex flex-wrap items-center gap-x-2">Before (original)
                  {imgW > 0 && <span className={`font-mono ${dpiVal >= 300 ? "text-emerald-600" : dpiVal >= 150 ? "text-amber-600" : "text-red-600"}`} data-testid="fit-size-dpi">· {imgW}×{imgH}px · {dpiVal} DPI @ {ow}×{oh}{unit}{dpiVal < 150 ? " (low)" : dpiVal >= 300 ? " (print-ready)" : ""}</span>}
                </div>
                <div className="rounded-lg border bg-[repeating-conic-gradient(#f1f5f9_0%_25%,#fff_0%_50%)] bg-[length:16px_16px] h-56 flex items-center justify-center overflow-hidden">
                  <img src={imgSrc} alt="original" className="max-h-full max-w-full object-contain" />
                </div>
              </div>
              <div>
                <div className="text-[11px] text-slate-400 mb-1">After — trim {w}×{h} {unit}{b > 0 ? ` · production size ${ow}×${oh} ${unit}` : ""} · {modeLabel}</div>
                <div className="h-56 flex items-center justify-center">
                  <div className="relative shadow-sm overflow-hidden" style={{ aspectRatio: `${ow} / ${oh}`, maxHeight: "14rem", maxWidth: "100%", height: oh >= ow ? "14rem" : "auto", width: ow > oh ? "100%" : "auto", backgroundColor: bgColor }}>
                    <img src={imgSrc} alt="preview" className="absolute inset-0 w-full h-full" style={imgStyle} />
                    <div className="absolute border-2 border-red-500 pointer-events-none" style={{ left: `${(b / ow) * 100}%`, right: `${(b / ow) * 100}%`, top: `${(b / oh) * 100}%`, bottom: `${(b / oh) * 100}%` }} data-testid="fit-size-trim-frame" />
                    {b > 0 && <span className="absolute top-1 left-1 text-[9px] font-mono text-red-600 bg-white/80 px-1 rounded">trim {w}×{h}</span>}
                    {grommetsOn && gd > 0 && grommetPoints(grommetLayout, w, h, gd).map((p, i) => (
                      <span key={i} className="absolute w-2.5 h-2.5 rounded-full border-2 shadow -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                        style={{ left: `${((b + p[0]) / ow) * 100}%`, top: `${((b + p[1]) / oh) * 100}%`, backgroundColor: grommetColor === "light" ? "#ffffff" : "#111827", borderColor: grommetColor === "light" ? "#111827" : "#ffffff" }} data-testid={`fit-size-grommet-dot-${i}`} />
                    ))}
                  </div>
                </div>
                {b > 0 && <p className="text-[10px] text-slate-400 mt-1"><b>Production PDF = {ow}×{oh} {unit}</b> (real print size, incl. bleed). Red frame = final cut ({w}×{h}); the extra {b}&quot; per side is bleed, trimmed after printing.</p>}
              </div>
            </div>
          ) : (
            <p className="text-xs text-slate-400">Live preview is available for image files (PNG/JPG). PDFs are placed on generation.</p>
          )}
        </div>
      )}

      {uploaded?.file_id && <InkEstimateCard fileId={uploaded.file_id} width={width} height={height} bleed={bleed} unit={unit} tab="fitsize" auto />}

      <Button onClick={generate} disabled={busy || !uploaded} data-testid="fit-size-generate">
        {busy ? <><Loader2 size={16} className="animate-spin mr-1.5" /> Working…</> : "Build print-ready PDF"}
      </Button>

      {result && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 flex items-center justify-between" data-testid="fit-size-result">
          <div className="text-sm"><b>{result.filename}</b><div className="text-xs text-slate-500">production size {result.final_w_in || result.width_in}×{result.final_h_in || result.height_in} in{result.bleed_in ? ` (trim ${result.width_in}×${result.height_in} + ${result.bleed_in}" bleed/side)` : ""} · {result.mode}{result.grommets && result.grommets !== "none" ? ` · grommets: ${result.grommets}` : ""}</div></div>
          <a href={artUrl(result.file_id)} target="_blank" rel="noreferrer" download>
            <Button size="sm" data-testid="fit-size-download"><FileDown size={16} className="mr-1.5" /> Download</Button>
          </a>
        </div>
      )}
      {result?.file_id && (
        <div className="flex justify-end" data-testid="fit-size-save-library">
          <SaveToLibraryButton fileId={result.file_id} defaultLabel={result.filename} />
        </div>
      )}
    </div>
  );
};

export default FitSizeTab;
